import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Building2, Users, Network, BarChart3, MapPin, Database } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../api';

function RankList({ title, badge, items, Icon }) {
  return (
    <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[#E5DCC4] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-[#4A7C5E]"/>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">{badge}</span>
        </div>
        <h3 className="text-sm font-semibold text-[#1A2622]">{title}</h3>
      </div>
      <ol>
        {items.map((it, i) => (
          <li key={it.to || `${it.title}-${i}`}>
            <Link to={it.to} className="flex items-center justify-between px-5 py-3 border-t border-[#F0E7D2] first:border-t-0 hover:bg-[#FBF6EC] group">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-[11px] font-mono text-[#A8B0AA] w-5">{String(i+1).padStart(2,'0')}</span>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-[#1A2622] truncate group-hover:text-[#2D5F4F]">{it.title}</div>
                  <div className="text-[11px] text-[#7A857F] mt-0.5">{it.sub}</div>
                </div>
              </div>
              <span className="text-[12px] font-semibold text-[#1A2622] shrink-0">{it.right}</span>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function HomePage() {
  const [q, setQ] = useState('');
  const [stats, setStats] = useState({ facilities: 0, owners: 0 });
  const [rankings, setRankings] = useState({ top_facilities: [], top_owners: [], top_chains: [] });

  useEffect(() => {
    api.stats().then(setStats).catch(() => {});
    api.rankings().then(setRankings).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) window.location.href = `/facilities?q=${encodeURIComponent(q)}`;
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10 pb-10">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A7C5E]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C5E]"/>
          Live from data.cms.gov
        </div>

        <h1 className="mt-5 font-display text-[44px] md:text-[56px] leading-[1.04] tracking-tight text-[#1A2622] max-w-4xl">
          Explore U.S. nursing home ownership, chains, and facility performance.
        </h1>
        <p className="mt-5 text-[17px] text-[#3A4A44] max-w-2xl leading-relaxed">
          Search once, then follow the ownership graph from facility to operator to peers. All data sourced from public CMS releases, refreshed monthly.
        </p>

        <form onSubmit={onSearch} className="mt-8 flex items-center gap-3 bg-white border border-[#E5DCC4] rounded-xl px-5 h-[60px] shadow-soft focus-within:border-[#4A7C5E] transition-colors">
          <Search size={18} className="text-[#7A857F]"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by facility, city, or state…" className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-[#A8B0AA] text-[#1A2622]"/>
          <button type="submit" className="hidden md:flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#1A2622] text-[#FBF6EC] text-sm font-medium hover:bg-[#2D5F4F]">Search <ArrowRight size={14}/></button>
          <div className="hidden md:flex items-center gap-1.5 text-xs text-[#7A857F] border-l border-[#E5DCC4] pl-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C5E]"/>
            {(stats.facilities || 0).toLocaleString()} indexed
          </div>
        </form>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#7A857F] mr-1">Try</span>
          {['Dallas', 'Phoenix', 'Brookdale', 'Miami'].map((p) => (
            <Link key={p} to={`/facilities?q=${encodeURIComponent(p)}`} className="px-3 py-1 rounded-full border border-[#D4C9AF] bg-white text-xs text-[#3A4A44] hover:border-[#4A7C5E] hover:text-[#1A2622]">{p}</Link>
          ))}
        </div>

        {/* Directory tiles */}
        <section className="mt-14">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A7C5E]">Directories</div>
          <h2 className="mt-1.5 font-display text-2xl font-semibold text-[#1A2622]">Browse the catalog</h2>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { to: '/facilities', title: 'All facilities', n: stats.facilities, Icon: Building2 },
              { to: '/owners', title: 'All owners', n: stats.owners, Icon: Users },
              { to: '/chains', title: 'CMS chains', n: rankings.top_chains.length > 0 ? '619' : '—', Icon: Network },
            ].map((d) => {
              const Icon = d.Icon;
              return (
                <Link key={d.to} to={d.to} className="group flex items-center justify-between p-5 rounded-xl bg-white border border-[#E5DCC4] hover:border-[#4A7C5E] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#F0E7D2] flex items-center justify-center">
                      <Icon size={18} className="text-[#4A7C5E]"/>
                    </div>
                    <div>
                      <div className="text-[15px] font-semibold text-[#1A2622]">{d.title}</div>
                      <div className="text-xs text-[#7A857F] mt-0.5">{(typeof d.n === 'number' ? d.n.toLocaleString() : d.n)} records</div>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-[#A8B0AA] group-hover:text-[#4A7C5E] group-hover:translate-x-0.5 transition-all"/>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Leaderboards */}
        <section className="mt-12">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A7C5E]">Leaderboards</div>
          <h2 className="mt-1.5 font-display text-2xl font-semibold text-[#1A2622]">Today at the top</h2>
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <RankList
              title="Top facilities" badge="By rating" Icon={Building2}
              items={rankings.top_facilities.slice(0,5).map(f => ({ to:`/facilities/${f.slug}`, title:f.name, sub:`${f.city || ''}${f.city && f.state ? ', ' : ''}${f.state || ''}`, right: `★ ${f.rating}` }))}
            />
            <RankList
              title="Largest owners" badge="By portfolio" Icon={Users}
              items={rankings.top_owners.slice(0,5).map(o => ({ to:`/owners/${o.slug}`, title:o.name, sub:`${o.states} ${o.states===1?'state':'states'} · ${o.type || ''}`, right:`${o.facilities}` }))}
            />
            <RankList
              title="Biggest chains" badge="By facilities" Icon={Network}
              items={rankings.top_chains.slice(0,5).map(c => ({ to:`/chains/${c.slug}`, title:c.name, sub:c.rating ? `Rating ${c.rating}` : 'Multi-facility operator', right:`${c.facilities}` }))}
            />
          </div>
        </section>

        {/* Provenance */}
        <section className="mt-12">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A7C5E]">The dataset</div>
          <h2 className="mt-1.5 font-display text-2xl font-semibold text-[#1A2622]">Sourced from public CMS releases</h2>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { n: (stats.facilities || 0).toLocaleString(), l: 'Certified facilities' },
              { n: (stats.owners || 0).toLocaleString(), l: 'Distinct owners' },
              { n: (stats.associations || 0).toLocaleString(), l: 'Ownership records' },
              { n: '53', l: 'States & territories' },
            ].map((s) => (
              <div key={s.l} className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
                <div className="font-display text-3xl text-[#1A2622]">{s.n}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-[#7A857F]">{s.l}</div>
              </div>
            ))}
          </div>
          <a href="https://data.cms.gov" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#4A7C5E] hover:text-[#2D5F4F]"><Database size={13}/> data.cms.gov →</a>
        </section>
      </main>
      <Footer />
    </div>
  );
}
